<template>
  <div ref="raceBlockHeight" :class="$style.racesBlock">
    <span :class="[$style.racesWrapperTitle]">
      {{ data.blockTitle }}
    </span>
    <template v-if="!isLoading">
      <div v-if="tournamentsActiveNowList.length" :class="$style.racesBlockContent">
        <div :class="$style.racesBlockContentList">
          <h3 :class="$style.racesBlockContentTitle">
            {{ data.activeTitle }}
          </h3>
          <div :class="$style.racesPageBlock">
            <RaceCard
              v-for="race in tournamentsActiveNowList"
              :key="race.raceData.ssId"
              :class="$style.racesPageBlockItem"
              :race-ss-id="race.raceData.ssId"
              :data="data"
            />
          </div>
        </div>
        <div v-if="tournamentsComingSoonList.length" :class="$style.racesBlockContentList">
          <h3 :class="$style.racesBlockContentTitle">
            {{ data.scheduledTitle }}
          </h3>
          <div :class="$style.racesPageBlock">
            <RaceCard
              v-for="race in tournamentsComingSoonList"
              :key="race.raceData.ssId"
              :class="$style.racesPageBlockItem"
              :race-ss-id="race.raceData.ssId"
              is-coming-soon
              :data="data"
            />
          </div>
        </div>
      </div>
      <div v-else>
        <div :class="$style.racesBlockContentList">
          <h3 :class="$style.racesBlockContentTitle">
            {{ data.activeTitle }}
          </h3>
          <div :class="$style.racesPageBlockNoRaces">
            <img src="/images/common/ico_no_races.svg?url_encode" alt="No races" />
            <p>
              {{ data.noRacesText }}
            </p>
          </div>
        </div>
      </div>
    </template>
    <RacesBlockSkeleton v-else />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import RacesBlockSkeleton from '~/components/infoPage/racesBlockSkeleton.vue';
import RaceCard from '~/components/race/card.vue';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreSsTournaments } from '~/stores/softswiss/tournaments';
import { useStoreRaces } from '~/stores/strapi/races';
import { useStoreRacesBanner } from '~/stores/strapi/racesBanner';
import { ERaceStatus } from '~/types/races';
import type { Ref } from 'vue';
import type { ComponentInfoPageBlocksRacesBlock } from '~/types/infoPages';

defineProps({
  data: {
    type: Object as PropType<ComponentInfoPageBlocksRacesBlock>,
    required: true,
  },
});

const storeSsTournaments = useStoreSsTournaments();
const storeRaces = useStoreRaces();
const storeSsPlayer = useStoreSsPlayer();
const storeRacesBanners = useStoreRacesBanner();

const isLoading = ref(true);

const racesStatusToShow: Ref<ERaceStatus> = ref(ERaceStatus.active);

const tournamentsActiveNowList = computed(() => {
  return storeSsTournaments.tournamentsActive;
});
const tournamentsComingSoonList = computed(() => {
  return storeSsTournaments.tournamentsScheduled;
});

const pageBanners = computed(() => {
  return storeRacesBanners.personalList.map((banner) => ({
    buttonText: banner.buttonTitle,
    frontImage: banner.bannerCardFront,
    subtitleFirst: banner.subtitleFirst,
    subtitleSecond: banner.subtitleSecond,
    bannerAction: banner.bannerAction,
  }));
});

const scrollTopTabs = () => {
  if (!import.meta.browser || !window) {
    return;
  }
  if (pageBanners.value.length) {
    window.scrollTo({
      top: useStoreRoot().screen.width < 768 ? 420 : 428,
      behavior: 'smooth',
    });
  } else {
    window.scrollTo({
      top: useStoreRoot().screen.width < 768 ? 46 : 56,
      behavior: 'smooth',
    });
  }
};

const setRaceStatusToShow = (status: ERaceStatus) => {
  racesStatusToShow.value = status;
  scrollTopTabs();
};

watch(
  () => storeSsPlayer.isPlayerAuthorised,
  () => {
    if (storeSsPlayer.isPlayerAuthorised) {
      storeSsTournaments.loadPlayerStatusesInTournaments();
    }
  }
);

onMounted(async () => {
  isLoading.value = true;
  await Promise.all([storeSsTournaments.loadTournaments(), storeRaces.loadRacesData()]);

  if (!storeSsTournaments.tournamentsStatuses.length) {
    await storeSsTournaments.loadPlayerStatusesInTournaments();
  }

  if (
    !storeSsTournaments.tournamentsActive.length &&
    storeSsTournaments.tournamentsScheduled.length
  ) {
    setRaceStatusToShow(ERaceStatus.scheduled);
  }
  isLoading.value = false;
});
</script>

<style lang="scss" module src="./racesBlock.scss"></style>
